import { useMemo } from 'react';
import { PaymentsAbTestOrg } from '@wetags/payments-ab-test';
import { InvoiceModel } from '@frontend/api-invoices';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { paymentsSentry } from '@frontend/payments-hooks';
import { ChipProps, formatDate, SkeletonLoader } from '@frontend/design-system';
import { InvoiceCardDetails as InvoiceCardDetailsV1 } from './invoice-card/v1/invoice-card-details';
import { InvoiceCardDetails as InvoiceCardDetailsV2 } from './invoice-card/v2/invoice-card-details';

const getFormattedDate = (date: string) => {
  const currentYear = new Date().getFullYear();
  const d = new Date(date);

  if (d.getFullYear() === currentYear) {
    return formatDate(date, 'MMMM D', true);
  }
  return formatDate(date, 'MMM D, YYYY', true);
};

export type InvoiceCardProps = React.PropsWithChildren<{
  invoice: InvoiceModel;
  className?: string;
}>;

export function InvoiceCard({ invoice, className, children }: InvoiceCardProps) {
  const { t } = useTranslation();
  const [newInvoiceCard, isLoading] = PaymentsAbTestOrg.useWetag('payments-panel-new-invoice-card', {});

  const formattedBilledAt = useMemo(() => {
    return getFormattedDate(invoice.billedAt);
  }, [invoice]);

  const formattedBilledAmount = useMemo(() => {
    return formatCentsToCurrency(invoice.billedAmount, false);
  }, [invoice]);

  const formattedOwedAmount = useMemo(() => {
    return formatCentsToCurrency(invoice.billedAmount - (invoice.payment?.paidAmount || 0), false);
  }, [invoice]);

  const billedAtData = useMemo(() => {
    try {
      const d = new Date(invoice.billedAt);
      const n = new Date();

      // get count of days passed
      const daysPassed =
        Math.floor((new Date().getTime() - d.getTime()) / (1000 * 3600 * 24)) + (d.getDate() === n.getDate() ? 0 : 1);

      let variant: ChipProps['variant'] = 'neutral';
      let daysPassedLabel = '';
      if (daysPassed === 1) {
        daysPassedLabel = t(`1 Day`);
      } else if (daysPassed < 30) {
        daysPassedLabel = t(`{{ days }} Days`, { days: daysPassed });
      } else if (daysPassed < 60) {
        variant = 'warn';
        daysPassedLabel = t(`{{ days }}+ Days`, { days: 30 });
      } else if (daysPassed < 90) {
        variant = 'tangerine';
        daysPassedLabel = t(`{{ days }}+ Days`, { days: 60 });
      } else {
        variant = 'critical';
        daysPassedLabel = t(`{{ days }}+ Days`, { days: 90 });
      }

      return {
        variant,
        daysPassedLabel,
      };
    } catch (error) {
      paymentsSentry.error(
        'Failed to calculate days passed',
        'InvoiceCard',
        error instanceof Error ? error.message : 'Unknown error calculating days passed'
      );
      return {
        variant: 'neutral' as ChipProps['variant'],
        daysPassedLabel: '',
      };
    }
  }, [invoice, t]);

  const InvoiceCardDetails = useMemo(
    () => (newInvoiceCard ? InvoiceCardDetailsV2 : InvoiceCardDetailsV1),
    [newInvoiceCard]
  );

  if (isLoading) {
    return <SkeletonLoader width='100%' height='200px' count={1} distance={20} animation='shimmer' />;
  }

  return (
    <InvoiceCardDetails
      className={className}
      invoice={invoice}
      formattedBilledAt={formattedBilledAt}
      billedAtData={billedAtData}
      formattedBilledAmount={formattedBilledAmount}
      formattedOwedAmount={formattedOwedAmount}
    >
      {children}
    </InvoiceCardDetails>
  );
}
